/**
 * Quality assessment dla wpisu WP.
 * Reguly heurystyczne (bez LLM, ~100ms per wpis).
 * LLM stage 2 (opt-in) — placeholder, prompt prześle Darek.
 */

import type { QualityAssessment, WpHtmlMetrics } from "@/types/thematicMaps";
import { analyzeWpHtml } from "./wpHtmlAnalyzer";

/**
 * Minimalny kontrakt na input — akceptujemy dowolny ksztalt zblizony do WpPost
 * (z roznych zrodel: DB row, WpPostRaw po transform).
 */
export interface AssessablePost {
  content: string | null | undefined;
  published_at?: string | null;
  modified_at?: string | null;
  meta_description?: string | null;
  featured_image_url?: string | null;
}

const EMPTY_METRICS: WpHtmlMetrics = {
  word_count: 0, char_count: 0,
  h1_count: 0, h2_count: 0, h3_count: 0, h4_count: 0,
  paragraph_count: 0, list_count: 0, image_count: 0,
  outbound_links: [], internal_links: [],
  plaintext: "", first_paragraph: "",
  keyword_stuffing_score: 100, top_words: [],
};

/**
 * Heurystyczna ocena jakosci.
 * @param post — obiekt z polami co najmniej: content, published_at, modified_at, meta_description, featured_image_url
 * @param siteDomain — domena (do rozroznienia outbound vs internal linkow)
 */
export function assessWpPostQuality(
  post: AssessablePost,
  siteDomain: string,
): QualityAssessment {
  if (!post.content || typeof post.content !== "string" || post.content.trim().length === 0) {
    return {
      score: 0,
      red_flags: ["no_content"],
      recommendation: "refurbish",
      metrics: { ...EMPTY_METRICS },
      assessed_at: new Date().toISOString(),
      method: "heuristic",
    };
  }

  const metrics = analyzeWpHtml(post.content, siteDomain);
  const red_flags: string[] = [];
  let score = 100;

  // Rule 1: Word count
  if (metrics.word_count < 300) {
    red_flags.push("too_short");
    score -= 30;
  } else if (metrics.word_count < 600) {
    red_flags.push("thin_content");
    score -= 15;
  } else if (metrics.word_count > 1200) {
    score += 5;
  }

  // Rule 2: Struktura
  if (metrics.h2_count === 0) {
    red_flags.push("no_h2_structure");
    score -= 20;
  } else if (metrics.h2_count < 2) {
    red_flags.push("weak_structure");
    score -= 10;
  }

  if (metrics.h3_count === 0 && metrics.word_count > 800) {
    red_flags.push("flat_structure");
    score -= 5;
  }

  if (metrics.list_count === 0 && metrics.word_count > 600) {
    red_flags.push("no_lists");
    score -= 5;
  }

  // Rule 3: Keyword stuffing
  if (metrics.keyword_stuffing_score < 40) {
    red_flags.push("heavy_stuffing");
    score -= 25;
  } else if (metrics.keyword_stuffing_score < 60) {
    red_flags.push("keyword_stuffed");
    score -= 15;
  }

  // Rule 4: Linki
  if (metrics.outbound_links.length === 0) {
    red_flags.push("no_outbound_links");
    score -= 5;
  }
  if (metrics.internal_links.length === 0) {
    red_flags.push("no_internal_links");
    score -= 10;
  }

  // Rule 5: Wiek i swiezosc
  if (post.published_at) {
    const publishedDate = new Date(post.published_at);
    const modifiedDate = post.modified_at ? new Date(post.modified_at) : null;
    const ageYears = (Date.now() - publishedDate.getTime()) / (1000 * 60 * 60 * 24 * 365);
    const lastModifiedMonths = modifiedDate
      ? (Date.now() - modifiedDate.getTime()) / (1000 * 60 * 60 * 24 * 30)
      : ageYears * 12;

    if (ageYears > 3 && lastModifiedMonths > 12) {
      red_flags.push("stale_content");
      score -= 10;
    }
  }

  // Rule 6: Meta signals
  if (!post.meta_description || post.meta_description.length < 50) {
    red_flags.push("no_meta");
    score -= 5;
  }
  if (!post.featured_image_url) {
    red_flags.push("no_featured_image");
    score -= 3;
  }
  if (metrics.image_count === 0 && metrics.word_count > 500) {
    red_flags.push("no_images_in_content");
    score -= 3;
  }

  // Clamp 0-100
  score = Math.max(0, Math.min(100, score));

  // Recommendation
  let recommendation: "keep" | "borderline" | "refurbish";
  if (score >= 65) recommendation = "keep";
  else if (score >= 45) recommendation = "borderline";
  else recommendation = "refurbish";

  return {
    score,
    red_flags,
    recommendation,
    metrics,
    assessed_at: new Date().toISOString(),
    method: "heuristic",
  };
}
